/**
 * Alertes GPS, zones et commandes Traccar (migration 0052) — logique PURE (testée dans `alertes.test.ts`) puis les appels.
 *
 * Les règles sont évaluées PAR LA BASE à l'arrivée de chaque position (SQL, `gps_evaluer_position`), jamais par le navigateur :
 * ce fichier décrit, règle et affiche. Les bornes de `SCHEMA_PARAMETRES` sont celles de `gps_regle_schema()` côté base (qui refuse
 * le reste : `gps_parametre_invalide`) ; `alertes.test.ts` relit la migration pour vérifier qu'elles ne dérivent pas.
 *
 * Les commandes passent TOUJOURS par l'edge function `traccar-commande` (qui seule parle à Traccar) : le navigateur n'appelle
 * jamais Traccar.
 *
 * Même module que celui du portail SaaS (`webapp-ts/src/api/alertes.ts`) ; seules changent les classes de pastille
 * (`badge b-*` de cette console au lieu de `p p-*`). La console opérateur est l'organisation opératrice : exemptée de palier
 * (`org_tier` = business), donc les messages « Pro et Business » ne la concernent pas en pratique.
 */
import { appelerFonction, supabase } from './supabase'

/* ------------------------------------------------------------------ règles */

export type TypeRegle = 'exces_vitesse' | 'zone' | 'arret_prolonge' | 'hors_ligne' | 'alarme' | 'vitesse_dangereuse'
export type Severite = 'info' | 'attention' | 'urgent'
export const TYPES_REGLE: readonly TypeRegle[] = ['exces_vitesse', 'zone', 'arret_prolonge', 'hors_ligne', 'alarme', 'vitesse_dangereuse']
export const SEVERITES: readonly Severite[] = ['info', 'attention', 'urgent']
export const LIBELLE_SEVERITE: Record<Severite, string> = { info: 'Information', attention: 'Attention', urgent: 'Urgent' }
export const PASTILLE_SEVERITE: Record<Severite, string> = { info: 'b-blue', attention: 'b-warn', urgent: 'b-red' }

export interface ChampParametre {
  cle: string
  libelle: string
  unite: string
  aide: string
  /** Absent pour un booléen. */
  min?: number
  max?: number
  def: number | boolean
  booleen?: boolean
}

/** Miroir de `gps_regle_schema()` (0052) : bornes et valeurs par défaut. */
export const SCHEMA_PARAMETRES: Record<TypeRegle, readonly ChampParametre[]> = {
  exces_vitesse: [
    { cle: 'limite_kmh', libelle: 'Limite de vitesse', unite: 'km/h', aide: 'La vitesse au-delà de laquelle on parle d’excès.', min: 10, max: 250, def: 90 },
    { cle: 'tolerance_kmh', libelle: 'Tolérance', unite: 'km/h', aide: 'Marge ajoutée à la limite (imprécision du GPS) : l’alerte part au-dessus de limite + tolérance.', min: 0, max: 30, def: 5 },
    { cle: 'duree_min_s', libelle: 'Durée minimale au-dessus', unite: 's', aide: 'Évite les fausses alertes : le dépassement doit durer au moins ce temps (0 = dès la première position).', min: 0, max: 600, def: 30 },
    { cle: 'repos_min', libelle: 'Délai de repos', unite: 'min', aide: 'Un dépassement qui reprend dans ce délai rouvre la MÊME alerte au lieu d’en créer une autre.', min: 0, max: 120, def: 10 },
  ],
  zone: [
    { cle: 'confirmation', libelle: 'Positions de confirmation', unite: 'positions', aide: 'Nombre de positions consécutives de l’autre côté de la limite avant d’alerter (bruit du GPS en bordure de zone).', min: 1, max: 5, def: 2 },
    { cle: 'repos_min', libelle: 'Délai de repos', unite: 'min', aide: 'Un aller-retour rapide autour de la limite rouvre la même alerte.', min: 0, max: 120, def: 5 },
  ],
  arret_prolonge: [
    { cle: 'duree_min', libelle: 'Durée d’arrêt', unite: 'min', aide: 'Le véhicule est à l’arrêt, moteur allumé, depuis au moins ce temps.', min: 2, max: 720, def: 15 },
    { cle: 'vitesse_max_kmh', libelle: 'Vitesse considérée comme « arrêt »', unite: 'km/h', aide: 'En dessous de cette vitesse, le véhicule est à l’arrêt.', min: 0, max: 10, def: 3 },
    { cle: 'exiger_contact', libelle: 'Seulement contact mis', unite: '', aide: 'Si coché, rien n’est signalé tant que le contact (ACC) n’est pas connu comme mis.', def: true, booleen: true },
  ],
  hors_ligne: [
    { cle: 'duree_min', libelle: 'Silence du boîtier', unite: 'min', aide: 'Aucun message reçu du boîtier depuis ce temps.', min: 5, max: 1440, def: 30 },
    { cle: 'abandon_jours', libelle: 'Abandon', unite: 'jours', aide: 'Un boîtier silencieux depuis plus longtemps est considéré comme retiré : l’alerte se referme et ne se rouvre pas.', min: 1, max: 90, def: 7 },
  ],
  alarme: [{ cle: 'repos_min', libelle: 'Regroupement', unite: 'min', aide: 'La même alarme répétée dans ce délai se regroupe en une seule alerte.', min: 0, max: 120, def: 10 }],
  vitesse_dangereuse: [
    { cle: 'freinage', libelle: 'Détecter les freinages brusques', unite: '', aide: 'Décélération calculée entre deux positions consécutives.', def: true, booleen: true },
    { cle: 'decel_kmh_s', libelle: 'Décélération brusque', unite: 'km/h par s', aide: 'Perte de vitesse par seconde à partir de laquelle un freinage est jugé brusque (10 km/h/s ≈ 0,28 g).', min: 4, max: 40, def: 10 },
    { cle: 'vitesse_min_kmh', libelle: 'Vitesse minimale avant freinage', unite: 'km/h', aide: 'On ne regarde que les freinages partis de cette vitesse au moins.', min: 20, max: 200, def: 50 },
    { cle: 'virage', libelle: 'Détecter la vitesse en virage', unite: '', aide: 'Changement de cap important à vitesse élevée entre deux positions consécutives.', def: true, booleen: true },
    { cle: 'virage_angle_deg', libelle: 'Changement de cap', unite: '°', aide: 'Angle à partir duquel on parle de virage.', min: 15, max: 120, def: 40 },
    { cle: 'vitesse_virage_kmh', libelle: 'Vitesse en virage', unite: 'km/h', aide: 'Vitesse minimale (sur les deux positions) pour juger le virage dangereux.', min: 20, max: 150, def: 60 },
    { cle: 'ecart_max_s', libelle: 'Écart maximal entre deux positions', unite: 's', aide: 'Au-delà, le calcul n’est plus fiable : la règle se tait. Il faut un boîtier qui envoie une position toutes les 10 à 20 s.', min: 5, max: 60, def: 20 },
    { cle: 'repos_min', libelle: 'Regroupement', unite: 'min', aide: 'Les événements répétés dans ce délai se regroupent.', min: 0, max: 120, def: 5 },
  ],
}

export const LIBELLE_TYPE_REGLE: Record<TypeRegle, string> = {
  exces_vitesse: 'Excès de vitesse',
  zone: 'Entrée / sortie de zones',
  arret_prolonge: 'Arrêt prolongé moteur allumé',
  hors_ligne: 'Boîtier hors ligne',
  alarme: 'Alarmes du boîtier',
  vitesse_dangereuse: 'Vitesse dangereuse (estimée)',
}

export const DESCRIPTION_TYPE_REGLE: Record<TypeRegle, string> = {
  exces_vitesse: 'Alerte quand un véhicule dépasse la limite plus la tolérance pendant la durée minimale.',
  zone: 'Alerte à la SORTIE d’une zone « autorisée », à l’ENTRÉE d’une zone « interdite », aux deux pour une zone « surveillée ». Les zones se dessinent dans l’onglet Zones.',
  arret_prolonge: 'Alerte quand un véhicule reste à l’arrêt, moteur allumé, trop longtemps.',
  hors_ligne: 'Alerte quand un boîtier ne donne plus de nouvelles (coupure, batterie, zone sans réseau).',
  alarme: 'Relaie les alarmes que le boîtier remonte lui-même : SOS, coupure d’alimentation, remorquage, collision, freinage brusque… selon son modèle.',
  vitesse_dangereuse: 'Estime les freinages brusques et les virages pris trop vite à partir de deux positions consécutives (le boîtier ne mesure pas l’accélération). Fiable seulement avec une position toutes les 10 à 20 s.',
}

/** Réglage de départ proposé à la création d'une règle de ce type. */
export const DEFAUTS_REGLE: Record<TypeRegle, { nom: string; severite: Severite }> = {
  exces_vitesse: { nom: 'Limite de vitesse', severite: 'attention' },
  zone: { nom: 'Zones', severite: 'urgent' },
  arret_prolonge: { nom: 'Arrêt prolongé', severite: 'info' },
  hors_ligne: { nom: 'Boîtier hors ligne', severite: 'attention' },
  alarme: { nom: 'Alarmes du boîtier', severite: 'urgent' },
  vitesse_dangereuse: { nom: 'Conduite dangereuse', severite: 'attention' },
}

/** Codes d'alarme de Traccar (`Position.ALARM_*`) et leur libellé (miroir de `gps_libelle_alarme`, 0052). */
export const LIBELLE_ALARME: Record<string, string> = {
  general: 'Alarme générale', sos: 'SOS', vibration: 'Vibration', movement: 'Mouvement', lowspeed: 'Vitesse basse', overspeed: 'Excès de vitesse (boîtier)',
  fallDown: 'Chute', lowPower: 'Batterie du véhicule faible', lowBattery: 'Batterie du boîtier faible', fault: 'Défaut', powerOff: 'Alimentation coupée',
  powerOn: 'Alimentation rétablie', door: 'Porte ouverte', lock: 'Verrouillage', unlock: 'Déverrouillage', geofence: 'Zone du boîtier',
  geofenceEnter: 'Entrée de zone (boîtier)', geofenceExit: 'Sortie de zone (boîtier)', gpsAntennaCut: 'Antenne GPS coupée', accident: 'Collision',
  tow: 'Remorquage', idle: 'Ralenti prolongé', highRpm: 'Régime moteur élevé', hardAcceleration: 'Accélération brusque', hardBraking: 'Freinage brusque',
  hardCornering: 'Virage brusque', laneChange: 'Changement de voie', fatigueDriving: 'Fatigue du conducteur', powerCut: 'Coupure d’alimentation',
  powerRestored: 'Alimentation rétablie (secteur)', jamming: 'Brouillage', temperature: 'Température', parking: 'Stationnement', bonnet: 'Capot ouvert',
  footBrake: 'Frein', fuelLeak: 'Fuite de carburant', tampering: 'Sabotage', removing: 'Boîtier arraché',
}
export const ALARMES_PAR_DEFAUT: readonly string[] = [
  'sos', 'powerCut', 'powerOff', 'tow', 'accident', 'hardBraking', 'hardAcceleration', 'hardCornering', 'lowBattery', 'removing', 'tampering', 'gpsAntennaCut',
]
/** Ce qu'un boîtier GT06 de base remonte réellement (lu dans le décodeur de Traccar, 14/10/2026) : le reste dépend du modèle. */
export const ALARMES_GT06_DE_BASE: readonly string[] = ['sos', 'powerCut', 'vibration', 'lowBattery', 'geofence', 'removing']

export interface Regle {
  id: string
  type: TypeRegle
  nom: string
  actif: boolean
  /** `null` = tout le parc. */
  deviceIds: string[] | null
  parametres: Record<string, unknown>
  severite: Severite
  canaux: { email: boolean; telegram: boolean }
}

export function valeurParametre(r: Pick<Regle, 'type' | 'parametres'>, cle: string): number | boolean {
  const champ = SCHEMA_PARAMETRES[r.type].find((c) => c.cle === cle)
  const v = r.parametres[cle]
  if (champ?.booleen) return typeof v === 'boolean' ? v : (champ.def as boolean)
  return typeof v === 'number' && Number.isFinite(v) ? v : ((champ?.def as number | undefined) ?? 0)
}

/** Valeurs affichées d'un formulaire : celles posées, sinon les valeurs par défaut. */
export function parametresComplets(type: TypeRegle, posees: Record<string, unknown>): Record<string, number | boolean> {
  const sortie: Record<string, number | boolean> = {}
  for (const c of SCHEMA_PARAMETRES[type]) sortie[c.cle] = valeurParametre({ type, parametres: posees }, c.cle)
  return sortie
}

/** Erreurs de saisie d'une règle (la base refuse de toute façon ; ceci évite l'aller-retour). */
export function erreursRegle(type: TypeRegle, nom: string, parametres: Record<string, unknown>): string[] {
  const e: string[] = []
  if (nom.trim().length < 1 || nom.trim().length > 80) e.push('Donnez un nom à la règle (80 caractères au plus).')
  for (const c of SCHEMA_PARAMETRES[type]) {
    const v = parametres[c.cle]
    if (v === undefined) continue
    if (c.booleen) {
      if (typeof v !== 'boolean') e.push(`« ${c.libelle} » : oui ou non.`)
    } else if (typeof v !== 'number' || !Number.isFinite(v) || v < (c.min ?? 0) || v > (c.max ?? 0)) {
      e.push(`« ${c.libelle} » : entre ${c.min} et ${c.max} ${c.unite}.`)
    }
  }
  if (type === 'alarme' && Array.isArray(parametres.alarmes)) {
    if (parametres.alarmes.length === 0) e.push('Choisissez au moins une alarme à surveiller.')
    if (parametres.alarmes.some((a) => typeof a !== 'string' || !(a in LIBELLE_ALARME))) e.push('Alarme inconnue.')
  }
  return e
}

/** Phrase de résumé d'une règle (liste des règles). */
export function resumeRegle(r: Regle): string {
  const n = (c: string) => valeurParametre(r, c)
  switch (r.type) {
    case 'exces_vitesse':
      return `Au-dessus de ${n('limite_kmh')} km/h (+ ${n('tolerance_kmh')} de tolérance) pendant ${n('duree_min_s')} s`
    case 'zone':
      return `Confirmée après ${n('confirmation')} position${Number(n('confirmation')) > 1 ? 's' : ''} de l’autre côté de la limite`
    case 'arret_prolonge':
      return `À l’arrêt (≤ ${n('vitesse_max_kmh')} km/h) depuis ${n('duree_min')} min${valeurParametre(r, 'exiger_contact') ? ', contact mis' : ''}`
    case 'hors_ligne':
      return `Aucun message depuis ${n('duree_min')} min`
    case 'alarme': {
      const l = alarmesDeLaRegle(r)
      return l.length <= 3 ? l.map((a) => LIBELLE_ALARME[a] ?? a).join(', ') : `${l.length} alarmes surveillées`
    }
    case 'vitesse_dangereuse': {
      const morceaux: string[] = []
      if (valeurParametre(r, 'freinage')) morceaux.push(`freinage ≥ ${n('decel_kmh_s')} km/h par s depuis ${n('vitesse_min_kmh')} km/h`)
      if (valeurParametre(r, 'virage')) morceaux.push(`virage ≥ ${n('virage_angle_deg')}° à ${n('vitesse_virage_kmh')} km/h`)
      return morceaux.length > 0 ? morceaux.join(' ; ') : 'Aucune détection activée'
    }
  }
}

export function alarmesDeLaRegle(r: Pick<Regle, 'parametres'>): string[] {
  const a = r.parametres.alarmes
  return Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : [...ALARMES_PAR_DEFAUT]
}

/** Boîtiers visés, en toutes lettres. */
export function portee(r: Pick<Regle, 'deviceIds'>, nomsBoitiers: ReadonlyMap<string, string>): string {
  if (r.deviceIds === null) return 'Tout le parc'
  const noms = r.deviceIds.map((id) => nomsBoitiers.get(id) ?? 'Boîtier retiré')
  return noms.length <= 2 ? noms.join(', ') : `${noms.length} boîtiers`
}

/* ------------------------------------------------------------------ zones */

export type ModeZone = 'autorisee' | 'interdite' | 'surveillee'
export const LIBELLE_MODE_ZONE: Record<ModeZone, string> = { autorisee: 'Autorisée (alerte à la sortie)', interdite: 'Interdite (alerte à l’entrée)', surveillee: 'Surveillée (entrée et sortie)' }
export const COULEUR_MODE_ZONE: Record<ModeZone, string> = { autorisee: '#0F8A6A', interdite: '#C0392B', surveillee: '#B7791F' }

export interface Zone {
  id: string
  nom: string
  forme: 'cercle' | 'polygone'
  centre: [number, number] | null
  rayonM: number | null
  /** [[lat, lng], …] */
  points: [number, number][] | null
  mode: ModeZone
  actif: boolean
  deviceIds: string[] | null
}

/** Distance de grand cercle en mètres — même formule que `gps_distance_m` (0052). */
export function distanceM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = (d: number) => (d * Math.PI) / 180
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Un point est-il dans la zone ? Même calcul que `gps_point_dans_zone` (cercle : haversine ; polygone : lancer de rayon). */
export function pointDansZone(z: Pick<Zone, 'forme' | 'centre' | 'rayonM' | 'points'>, lat: number, lng: number): boolean {
  if (z.forme === 'cercle') {
    if (!z.centre || z.rayonM === null) return false
    return distanceM(z.centre[0], z.centre[1], lat, lng) <= z.rayonM
  }
  const p = z.points
  if (!p || p.length < 3) return false
  let dedans = false
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [yi, xi] = p[i]!
    const [yj, xj] = p[j]!
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) dedans = !dedans
  }
  return dedans
}

export function erreursZone(z: { nom: string; forme: 'cercle' | 'polygone'; centre: [number, number] | null; rayonM: number | null; points: [number, number][] }): string[] {
  const e: string[] = []
  if (z.nom.trim().length < 1 || z.nom.trim().length > 80) e.push('Donnez un nom à la zone (80 caractères au plus).')
  if (z.forme === 'cercle') {
    if (!z.centre) e.push('Cliquez sur la carte pour placer le centre.')
    if (z.rayonM === null || !(z.rayonM >= 20 && z.rayonM <= 100_000)) e.push('Le rayon doit être compris entre 20 m et 100 km.')
  } else {
    if (z.points.length < 3) e.push('Un polygone a au moins 3 sommets : cliquez sur la carte pour les poser.')
    if (z.points.length > 100) e.push('Un polygone a 100 sommets au plus.')
  }
  return e
}

/* ------------------------------------------------------------------ alertes */

export type TypeAlerte = 'exces_vitesse' | 'zone_entree' | 'zone_sortie' | 'arret_prolonge' | 'hors_ligne' | 'alarme' | 'vitesse_dangereuse'
export const LIBELLE_TYPE_ALERTE: Record<TypeAlerte, string> = {
  exces_vitesse: 'Excès de vitesse',
  zone_entree: 'Entrée en zone',
  zone_sortie: 'Sortie de zone',
  arret_prolonge: 'Arrêt prolongé',
  hors_ligne: 'Hors ligne',
  alarme: 'Alarme du boîtier',
  vitesse_dangereuse: 'Vitesse dangereuse',
}

export interface AlerteGps {
  id: number
  deviceId: string
  boitierNom: string
  type: TypeAlerte
  nature: 'etat' | 'evenement'
  severite: Severite
  titre: string
  detail: Record<string, unknown>
  debutLe: string
  derniereOccurrenceLe: string
  nbOccurrences: number
  resolueLe: string | null
  resolueMotif: string | null
  acquitteeLe: string | null
  email: string | null
  telegram: string | null
}

export const COLONNES_ALERTE =
  'id,device_id,boitier_nom,type,nature,severite,titre,detail,debut_le,derniere_occurrence_le,nb_occurrences,resolue_le,resolue_motif,acquittee_le,email_statut,telegram_statut'

const texte = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const estType = (v: unknown): v is TypeAlerte => typeof v === 'string' && v in LIBELLE_TYPE_ALERTE
const estSeverite = (v: unknown): v is Severite => v === 'info' || v === 'attention' || v === 'urgent'

export function versAlerte(r: Record<string, unknown>): AlerteGps {
  return {
    id: Number(r.id),
    deviceId: String(r.device_id ?? ''),
    boitierNom: String(r.boitier_nom ?? ''),
    type: estType(r.type) ? r.type : 'alarme',
    nature: r.nature === 'etat' ? 'etat' : 'evenement',
    severite: estSeverite(r.severite) ? r.severite : 'attention',
    titre: String(r.titre ?? ''),
    detail: r.detail && typeof r.detail === 'object' ? (r.detail as Record<string, unknown>) : {},
    debutLe: String(r.debut_le ?? ''),
    derniereOccurrenceLe: String(r.derniere_occurrence_le ?? r.debut_le ?? ''),
    nbOccurrences: Number(r.nb_occurrences ?? 1),
    resolueLe: texte(r.resolue_le),
    resolueMotif: texte(r.resolue_motif),
    acquitteeLe: texte(r.acquittee_le),
    email: texte(r.email_statut),
    telegram: texte(r.telegram_statut),
  }
}

export type StatutAlerte = 'a_traiter' | 'ouvertes' | 'terminees' | 'toutes'
export interface FiltreAlertes {
  statut: StatutAlerte
  type: TypeAlerte | ''
  severite: Severite | ''
  deviceId: string
  /** Nombre de jours en arrière (0 = tout l'historique conservé : 90 jours). */
  jours: number
}
export const FILTRE_PAR_DEFAUT: FiltreAlertes = { statut: 'a_traiter', type: '', severite: '', deviceId: '', jours: 30 }

/** Le filtre en conditions PostgREST (colonne, opérateur, valeur), testé sans réseau. */
export function conditionsFiltre(f: FiltreAlertes, maintenant: number = Date.now()): [string, 'is' | 'not.is' | 'eq' | 'gte', string | null][] {
  const c: [string, 'is' | 'not.is' | 'eq' | 'gte', string | null][] = []
  if (f.statut === 'a_traiter') c.push(['acquittee_le', 'is', null])
  if (f.statut === 'ouvertes') c.push(['resolue_le', 'is', null])
  if (f.statut === 'terminees') c.push(['resolue_le', 'not.is', null])
  if (f.type) c.push(['type', 'eq', f.type])
  if (f.severite) c.push(['severite', 'eq', f.severite])
  if (f.deviceId) c.push(['device_id', 'eq', f.deviceId])
  if (f.jours > 0) c.push(['debut_le', 'gte', new Date(maintenant - f.jours * 86_400_000).toISOString()])
  return c
}

/** Le même filtre appliqué en local (aperçu) : mêmes conditions que `conditionsFiltre`. */
export function filtrerAlertes(l: readonly AlerteGps[], f: FiltreAlertes, maintenant: number = Date.now()): AlerteGps[] {
  const depuis = f.jours > 0 ? maintenant - f.jours * 86_400_000 : -Infinity
  return l.filter(
    (a) =>
      (f.statut !== 'a_traiter' || a.acquitteeLe === null) &&
      (f.statut !== 'ouvertes' || a.resolueLe === null) &&
      (f.statut !== 'terminees' || a.resolueLe !== null) &&
      (!f.type || a.type === f.type) &&
      (!f.severite || a.severite === f.severite) &&
      (!f.deviceId || a.deviceId === f.deviceId) &&
      Date.parse(a.debutLe) >= depuis,
  )
}

export function etatAlerte(a: Pick<AlerteGps, 'resolueLe' | 'acquitteeLe' | 'resolueMotif'>): { libelle: string; pastille: string } {
  if (a.resolueLe) {
    return { libelle: a.resolueMotif === 'acquittee' ? 'Acquittée' : 'Terminée', pastille: 'b-gray' }
  }
  if (a.acquitteeLe) return { libelle: 'Vue, en cours', pastille: 'b-blue' }
  return { libelle: 'À traiter', pastille: 'b-red' }
}

export function lienCarte(d: Record<string, unknown>): string | null {
  const lat = Number(d.lat)
  const lng = Number(d.lng)
  if (d.lat == null || d.lng == null || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return `https://www.openstreetmap.org/?mlat=${lat.toFixed(6)}&mlon=${lng.toFixed(6)}#map=16/${lat.toFixed(6)}/${lng.toFixed(6)}`
}

/** Canaux d'une alerte, en toutes lettres (« E-mail envoyé », « Telegram : non configuré »…). */
export function libelleCanal(canal: 'E-mail' | 'Telegram', statut: string | null): string | null {
  switch (statut) {
    case null: return null
    case 'a_envoyer': return `${canal} : en attente d’envoi`
    case 'envoye': return `${canal} : envoyé`
    case 'echec': return `${canal} : échec d’envoi`
    case 'ignore': return `${canal} : non envoyé (canal non configuré ou sans destinataire)`
    default: return `${canal} : ${statut}`
  }
}

/* ------------------------------------------------------------------ commandes */

export type TypeCommande = 'position_maintenant' | 'intervalle_positions' | 'redemarrer' | 'moteur_couper' | 'moteur_retablir' | 'fuseau' | 'apn'
export interface DefCommande {
  type: TypeCommande
  libelle: string
  aide: string
  dangereuse: boolean
  /** Propriétaire seulement. */
  proprietaire: boolean
}
export const COMMANDES: readonly DefCommande[] = [
  { type: 'position_maintenant', libelle: 'Demander la position maintenant', aide: 'Le boîtier répond par sa position actuelle (selon le modèle).', dangereuse: false, proprietaire: false },
  { type: 'intervalle_positions', libelle: 'Intervalle d’envoi des positions', aide: 'Plus l’intervalle est court, plus la carte est précise — et plus il y a de données (10 à 20 s pour la « vitesse dangereuse », 30 à 60 s pour une flotte urbaine).', dangereuse: false, proprietaire: false },
  { type: 'redemarrer', libelle: 'Redémarrer le boîtier', aide: 'Le boîtier se coupe quelques secondes puis se reconnecte.', dangereuse: false, proprietaire: false },
  { type: 'fuseau', libelle: 'Fuseau horaire du boîtier', aide: 'Décalage par rapport à l’heure universelle (le Mali est à +0 h).', dangereuse: false, proprietaire: false },
  { type: 'apn', libelle: 'APN de la carte SIM', aide: 'Nom du point d’accès de l’opérateur mobile (ex. orangeml.net). Un APN faux coupe le boîtier du réseau.', dangereuse: false, proprietaire: false },
  { type: 'moteur_couper', libelle: 'Couper le moteur', aide: 'Réservé au propriétaire. Seulement à l’arrêt (moins de 5 km/h, position de moins de 3 minutes).', dangereuse: true, proprietaire: true },
  { type: 'moteur_retablir', libelle: 'Rétablir le moteur', aide: 'Réservé au propriétaire.', dangereuse: true, proprietaire: true },
]

export const MOTS_DE_CONFIRMATION_MOTEUR = 'COUPER'

export type StatutCommande = 'demandee' | 'refusee' | 'envoyee' | 'hors_ligne' | 'acquittee' | 'echec' | 'simulee'
export const LIBELLE_STATUT_COMMANDE: Record<StatutCommande, { libelle: string; pastille: string }> = {
  demandee: { libelle: 'Demandée', pastille: 'b-gray' },
  refusee: { libelle: 'Refusée', pastille: 'b-red' },
  envoyee: { libelle: 'Envoyée', pastille: 'b-blue' },
  hors_ligne: { libelle: 'Boîtier hors ligne : en attente', pastille: 'b-warn' },
  acquittee: { libelle: 'Acquittée par le boîtier', pastille: 'b-green' },
  echec: { libelle: 'Échec', pastille: 'b-red' },
  simulee: { libelle: 'Simulée (rien n’est parti)', pastille: 'b-violet' },
}

export interface LigneJournal {
  id: number
  commandeId: string
  creeLe: string
  deviceId: string | null
  type: TypeCommande
  parametres: Record<string, unknown>
  statut: StatutCommande
  parNom: string | null
  detail: Record<string, unknown>
  simulation: boolean
}

export interface CommandeJournal {
  commandeId: string
  type: TypeCommande
  parametres: Record<string, unknown>
  /** Demande initiale. */
  creeLe: string
  parNom: string | null
  /** État le plus récent. */
  statut: StatutCommande
  detail: Record<string, unknown>
  historique: { statut: StatutCommande; le: string }[]
  simulation: boolean
}

export function versLigneJournal(r: Record<string, unknown>): LigneJournal {
  return {
    id: Number(r.id),
    commandeId: String(r.commande_id ?? ''),
    creeLe: String(r.created_at ?? ''),
    deviceId: texte(r.device_id),
    type: (texte(r.type) ?? 'position_maintenant') as TypeCommande,
    parametres: r.parametres && typeof r.parametres === 'object' ? (r.parametres as Record<string, unknown>) : {},
    statut: (texte(r.statut) ?? 'demandee') as StatutCommande,
    parNom: texte(r.par_nom),
    detail: r.detail && typeof r.detail === 'object' ? (r.detail as Record<string, unknown>) : {},
    simulation: r.simulation === true,
  }
}

/** Regroupe les lignes du journal (une par changement d'état) en commandes, la plus récente d'abord. */
export function commandesDuJournal(lignes: readonly LigneJournal[]): CommandeJournal[] {
  const parId = new Map<string, LigneJournal[]>()
  for (const l of lignes) parId.set(l.commandeId, [...(parId.get(l.commandeId) ?? []), l])
  const sortie: CommandeJournal[] = []
  for (const [commandeId, ls] of parId) {
    const tri = [...ls].sort((a, b) => a.id - b.id)
    const premiere = tri[0]!
    const derniere = tri[tri.length - 1]!
    sortie.push({
      commandeId, type: premiere.type, parametres: premiere.parametres, creeLe: premiere.creeLe, parNom: premiere.parNom,
      statut: derniere.statut, detail: derniere.detail, historique: tri.map((l) => ({ statut: l.statut, le: l.creeLe })), simulation: premiere.simulation,
    })
  }
  return sortie.sort((a, b) => (a.creeLe < b.creeLe ? 1 : a.creeLe > b.creeLe ? -1 : 0))
}

/** « Intervalle : toutes les 30 s », « Fuseau : UTC+0 h »… */
export function libelleParametresCommande(type: TypeCommande, p: Record<string, unknown>): string {
  switch (type) {
    case 'intervalle_positions': return `toutes les ${p.secondes} s`
    case 'fuseau': {
      const m = Number(p.decalage_minutes ?? 0)
      const h = Math.floor(Math.abs(m) / 60)
      const mn = Math.abs(m) % 60
      return `UTC${m < 0 ? '−' : '+'}${h}${mn ? ` h ${mn}` : ' h'}`
    }
    case 'apn': return String(p.apn ?? '')
    default: return ''
  }
}

export const MOTIFS_REFUS: Record<string, string> = {
  role_admin_requis: 'Seul le propriétaire du compte peut couper ou rétablir le moteur.',
  position_trop_ancienne: 'Dernière position trop ancienne : l’arrêt du véhicule n’est pas vérifiable.',
  vitesse_inconnue: 'Vitesse inconnue : l’arrêt du véhicule n’est pas vérifiable.',
  vehicule_en_mouvement: 'Le véhicule roule (5 km/h ou plus).',
  verification_impossible: 'Vitesse non vérifiable auprès de Traccar : moteur non coupé.',
  appareil_absent_du_suivi: 'Le boîtier n’existe pas encore dans Traccar : synchronisez-le.',
}

/** Erreur lisible d'un appel de fonction (messages de `gps-commande`). */
export function phraseCommande(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e)
  return m || 'La commande n’a pas pu être envoyée.'
}

/* ------------------------------------------------------------------ lectures et écritures */

const regleDepuis = (r: Record<string, unknown>): Regle => ({
  id: String(r.id),
  type: (TYPES_REGLE as readonly string[]).includes(String(r.type)) ? (r.type as TypeRegle) : 'alarme',
  nom: String(r.nom ?? ''),
  actif: r.actif !== false,
  deviceIds: Array.isArray(r.device_ids) ? (r.device_ids as unknown[]).map(String) : null,
  parametres: r.parametres && typeof r.parametres === 'object' ? (r.parametres as Record<string, unknown>) : {},
  severite: estSeverite(r.severite) ? r.severite : 'attention',
  canaux: {
    email: !!(r.canaux as { email?: unknown } | null)?.email,
    telegram: !!(r.canaux as { telegram?: unknown } | null)?.telegram,
  },
})
export { regleDepuis as versRegle }

const zoneDepuis = (r: Record<string, unknown>): Zone => ({
  id: String(r.id),
  nom: String(r.nom ?? ''),
  forme: r.forme === 'polygone' ? 'polygone' : 'cercle',
  centre: typeof r.centre_lat === 'number' && typeof r.centre_lng === 'number' ? [r.centre_lat, r.centre_lng] : null,
  rayonM: typeof r.rayon_m === 'number' ? r.rayon_m : null,
  points: Array.isArray(r.points) ? (r.points as [number, number][]) : null,
  mode: r.mode === 'interdite' || r.mode === 'surveillee' ? r.mode : 'autorisee',
  actif: r.actif !== false,
  deviceIds: Array.isArray(r.device_ids) ? (r.device_ids as unknown[]).map(String) : null,
})
export { zoneDepuis as versZone }

export interface ReglagesAlertes {
  emails: string[]
  telegramChatId: string | null
  telegramVerifie: boolean
}

function lancer(e: { message?: string; details?: string | null } | null): never {
  const brut = `${e?.message ?? ''}`
  const detail = e?.details ?? ''
  const phrases: Record<string, string> = {
    gps_parametre_invalide: detail || 'Un des paramètres est hors des limites autorisées.',
    gps_boitier_autre_organisation: 'Un des boîtiers n’appartient pas à votre organisation.',
    gps_trop_de_regles: 'Vous avez atteint 60 règles.',
    gps_trop_de_zones: 'Vous avez atteint 100 zones.',
    gps_zone_invalide: detail || 'La forme de la zone est invalide.',
    gps_email_invalide: detail || 'Adresse e-mail invalide.',
  }
  for (const [cle, phrase] of Object.entries(phrases)) if (brut.includes(cle)) throw new Error(phrase)
  if (/uq_gps_regles_parc/.test(brut)) throw new Error('Il existe déjà une règle de ce type pour tout le parc : modifiez-la, ou limitez celle-ci à certains boîtiers.')
  if (/row-level security|permission denied|forbidden/i.test(brut)) throw new Error('Votre rôle ou votre palier ne permet pas cette action (alertes : paliers Pro et Business, gestion : propriétaire ou dispatcher).')
  if (/plan_required/.test(brut)) throw new Error('Les alertes GPS sont réservées aux paliers Pro et Business.')
  throw new Error(brut || 'Opération impossible.')
}

export async function chargerRegles(orgId: string): Promise<Regle[]> {
  const { data, error } = await supabase.from('gps_alertes_regles').select('*').eq('org_id', orgId).order('cree_le')
  if (error) lancer(error)
  return ((data ?? []) as Record<string, unknown>[]).map(regleDepuis)
}

export interface SaisieRegle {
  type: TypeRegle
  nom: string
  actif: boolean
  deviceIds: string[] | null
  parametres: Record<string, unknown>
  severite: Severite
  canaux: { email: boolean; telegram: boolean }
}

export async function creerRegle(orgId: string, s: SaisieRegle): Promise<void> {
  const { error } = await supabase.from('gps_alertes_regles').insert({
    org_id: orgId, type: s.type, nom: s.nom.trim(), actif: s.actif, device_ids: s.deviceIds, parametres: s.parametres, severite: s.severite, canaux: s.canaux,
  })
  if (error) lancer(error)
}

export async function majRegle(id: string, patch: Partial<Omit<SaisieRegle, 'type'>>): Promise<void> {
  const p: Record<string, unknown> = {}
  if (patch.nom !== undefined) p.nom = patch.nom.trim()
  if (patch.actif !== undefined) p.actif = patch.actif
  if (patch.deviceIds !== undefined) p.device_ids = patch.deviceIds
  if (patch.parametres !== undefined) p.parametres = patch.parametres
  if (patch.severite !== undefined) p.severite = patch.severite
  if (patch.canaux !== undefined) p.canaux = patch.canaux
  const { error } = await supabase.from('gps_alertes_regles').update(p).eq('id', id)
  if (error) lancer(error)
}

export async function supprimerRegle(id: string): Promise<void> {
  const { error } = await supabase.from('gps_alertes_regles').delete().eq('id', id)
  if (error) lancer(error)
}

export async function chargerZones(orgId: string): Promise<Zone[]> {
  const { data, error } = await supabase.from('gps_zones').select('*').eq('org_id', orgId).order('cree_le')
  if (error) lancer(error)
  return ((data ?? []) as Record<string, unknown>[]).map(zoneDepuis)
}

export interface SaisieZone {
  nom: string
  forme: 'cercle' | 'polygone'
  centre: [number, number] | null
  rayonM: number | null
  points: [number, number][]
  mode: ModeZone
  actif: boolean
  deviceIds: string[] | null
}

function ligneZone(s: SaisieZone): Record<string, unknown> {
  return {
    nom: s.nom.trim(), forme: s.forme, mode: s.mode, actif: s.actif, device_ids: s.deviceIds,
    centre_lat: s.forme === 'cercle' ? s.centre?.[0] ?? null : null,
    centre_lng: s.forme === 'cercle' ? s.centre?.[1] ?? null : null,
    rayon_m: s.forme === 'cercle' ? s.rayonM : null,
    points: s.forme === 'polygone' ? s.points : null,
  }
}

export async function creerZone(orgId: string, s: SaisieZone): Promise<void> {
  const { error } = await supabase.from('gps_zones').insert({ org_id: orgId, ...ligneZone(s) })
  if (error) lancer(error)
}
export async function majZone(id: string, s: SaisieZone): Promise<void> {
  const { error } = await supabase.from('gps_zones').update(ligneZone(s)).eq('id', id)
  if (error) lancer(error)
}
export async function supprimerZone(id: string): Promise<void> {
  const { error } = await supabase.from('gps_zones').delete().eq('id', id)
  if (error) lancer(error)
}

export async function chargerAlertes(orgId: string, f: FiltreAlertes, limite = 200): Promise<AlerteGps[]> {
  let q = supabase.from('gps_alertes').select(COLONNES_ALERTE).eq('org_id', orgId)
  for (const [col, op, val] of conditionsFiltre(f)) {
    if (op === 'is') q = q.is(col, null)
    else if (op === 'not.is') q = q.not(col, 'is', null)
    else if (op === 'eq') q = q.eq(col, val as string)
    else q = q.gte(col, val as string)
  }
  const { data, error } = await q.order('debut_le', { ascending: false }).limit(limite)
  if (error) lancer(error)
  return ((data ?? []) as Record<string, unknown>[]).map(versAlerte)
}

export async function acquitterAlertes(orgId: string, ids: number[] | null): Promise<number> {
  const { data, error } = await supabase.rpc('gps_alertes_acquitter', { p_org: orgId, p_ids: ids })
  if (error) lancer(error)
  return Number(data ?? 0)
}

export interface CompteAlertes {
  aTraiter: number
  urgentes: number
  ouvertes: number
}
export async function compterAlertes(orgId: string): Promise<CompteAlertes | null> {
  const { data, error } = await supabase.rpc('gps_alertes_compte', { p_org: orgId })
  if (error || !data || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  return { aTraiter: Number(d.a_traiter ?? 0), urgentes: Number(d.urgentes ?? 0), ouvertes: Number(d.ouvertes ?? 0) }
}

export async function chargerReglages(orgId: string): Promise<ReglagesAlertes> {
  const { data, error } = await supabase.from('gps_alertes_reglages').select('emails,telegram_chat_id,telegram_verifie_le').eq('org_id', orgId).maybeSingle()
  if (error) lancer(error)
  const r = (data ?? {}) as Record<string, unknown>
  return {
    emails: Array.isArray(r.emails) ? (r.emails as unknown[]).map(String) : [],
    telegramChatId: texte(r.telegram_chat_id),
    telegramVerifie: !!r.telegram_verifie_le,
  }
}

export async function enregistrerReglages(orgId: string, s: { emails: string[]; telegramChatId: string | null }): Promise<void> {
  const { error } = await supabase
    .from('gps_alertes_reglages')
    .upsert({ org_id: orgId, emails: s.emails, telegram_chat_id: s.telegramChatId }, { onConflict: 'org_id' })
  if (error) lancer(error)
}

export async function testerCanal(orgId: string, canal: 'telegram' | 'email'): Promise<string> {
  const r = await appelerFonction<{ message?: string }>('gps-alertes-notifier', { action: 'tester_canal', org_id: orgId, canal })
  return r.message ?? 'Essai envoyé.'
}

/* commandes et nom (edge function gps-commande) */

export interface EtatTraccar {
  configure: boolean
  simulation: boolean
}
export async function etatTraccar(): Promise<EtatTraccar | null> {
  try {
    const r = await appelerFonction<{ suivi?: EtatTraccar }>('gps-commande', { action: 'etat' })
    return r.suivi ?? null
  } catch {
    return null
  }
}

export interface ReponseCommande {
  ok: boolean
  statut: StatutCommande
  commande_id: string
  message?: string
}
export function envoyerCommande(deviceId: string, type: TypeCommande, params: Record<string, unknown> = {}): Promise<ReponseCommande> {
  return appelerFonction<ReponseCommande>('gps-commande', { action: 'commande', device_id: deviceId, type, params })
}

export interface ReponseNom {
  ok: boolean
  nom: string | null
  suivi: 'cree' | 'renomme' | 'inchange' | 'simule' | 'non_configure' | 'echec'
  message?: string
}
export function nommerBoitier(deviceId: string, nom: string): Promise<ReponseNom> {
  return appelerFonction<ReponseNom>('gps-commande', { action: 'nommer', device_id: deviceId, nom })
}
export function synchroniserBoitier(deviceId: string): Promise<ReponseNom> {
  return appelerFonction<ReponseNom>('gps-commande', { action: 'synchroniser', device_id: deviceId })
}

export function phraseSynchro(r: Pick<ReponseNom, 'suivi' | 'message'>): string {
  switch (r.suivi) {
    case 'cree': return 'Nom enregistré, boîtier créé dans Traccar sous le même nom.'
    case 'renomme': return 'Nom enregistré, renommé à l’identique dans Traccar.'
    case 'inchange': return 'Nom enregistré (Traccar portait déjà ce nom).'
    case 'simule': return 'Nom enregistré (mode simulation : Traccar n’a pas été appelé).'
    case 'non_configure': return 'Nom enregistré. La synchronisation avec Traccar est en attente : Traccar n’est pas encore configuré.'
    default: return `Nom enregistré, mais Traccar n’a pas pu être mis à jour${r.message ? ` (${r.message})` : ''}. Réessayez avec « Synchroniser ».`
  }
}

export async function chargerJournalCommandes(deviceId: string, limite = 60): Promise<CommandeJournal[]> {
  const { data, error } = await supabase
    .from('gps_commandes_journal')
    .select('id,commande_id,created_at,device_id,type,parametres,statut,par_nom,detail,simulation')
    .eq('device_id', deviceId)
    .order('id', { ascending: false })
    .limit(limite * 4)
  if (error) lancer(error)
  return commandesDuJournal(((data ?? []) as Record<string, unknown>[]).map(versLigneJournal)).slice(0, limite)
}

export interface Liaison {
  extId: number | null
  nomEnvoye: string | null
  synchroniseLe: string | null
  erreur: string | null
}
export async function chargerLiaison(deviceId: string): Promise<Liaison | null> {
  const { data, error } = await supabase.from('gps_liaison_externe').select('ext_id,nom_envoye,synchronise_le,erreur').eq('device_id', deviceId).maybeSingle()
  if (error || !data) return null
  const r = data as Record<string, unknown>
  return { extId: typeof r.ext_id === 'number' ? r.ext_id : null, nomEnvoye: texte(r.nom_envoye), synchroniseLe: texte(r.synchronise_le), erreur: texte(r.erreur) }
}

/* ------------------------------------------------------------------ aperçu */

/** Ce que le mode aperçu (`?apercu=1`, développement seulement) fournit aux écrans à la place de la base. */
export interface FixturesAlertes {
  regles: Regle[]
  zones: Zone[]
  alertes: AlerteGps[]
  reglages: ReglagesAlertes
  journal: Record<string, CommandeJournal[]>
  compte: CompteAlertes
  suivi: EtatTraccar
}

/** Règles conseillées, créées d'un clic à la première visite : limite de vitesse, hors ligne, alarmes du boîtier. */
export const REGLES_CONSEILLEES: readonly SaisieRegle[] = [
  { type: 'exces_vitesse', nom: 'Limite de vitesse du parc', actif: true, deviceIds: null, parametres: {}, severite: 'attention', canaux: { email: false, telegram: false } },
  { type: 'hors_ligne', nom: 'Boîtier hors ligne', actif: true, deviceIds: null, parametres: {}, severite: 'attention', canaux: { email: false, telegram: false } },
  { type: 'alarme', nom: 'Alarmes du boîtier', actif: true, deviceIds: null, parametres: {}, severite: 'urgent', canaux: { email: false, telegram: false } },
]
